/**
 * Duplicate Investor Component (Wrapper)
 *
 * This is a minimal wrapper around the generic DuplicateEntity component
 * that handles investor-specific mutation logic.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client/react'
import { enqueueSnackbar } from 'notistack'
import DuplicateEntityComponent from './index.jsx'
import { INVESTOR_CONFIG } from './entityConfigs'
import { COPY_INVESTOR } from '@/housefolios-components/Investor/mutations'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_INVESTORS_BY_PORTFOLIO } from '@/housefolios-components/Investor/queries.js'

/**
 * Duplicate Investor Component
 *
 * @param {Object} props - Component props
 * @param {string} props.investorId - ID of the investor to duplicate
 * @param {Object} props.investor - The investor object to duplicate
 * @param {Array} props.portfolios - Available portfolios
 * @param {string} props.selectedPortfolio - Currently selected portfolio
 * @param {Function} props.setSelected - Function to clear selection after duplication
 * @param {Object} props.session - User session object
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onError - Error callback
 * @param {boolean} props.notifications - Whether to show as notification menu item
 * @param {boolean} props.rightClickMenu - Whether to show as right-click menu item
 * @param {string} props.buttonText - Custom button text
 */
function DuplicateInvestor(props) {
  const {
    investorId,
    investor,
    portfolios,
    selectedPortfolio: initialSelectedPortfolio,
    setSelected,
    session,
    onSuccess,
    onError,
    notifications = false,
    rightClickMenu = false,
    buttonText = '',
    ...otherProps
  } = props

  // Local state for portfolio selection
  const [selectedPortfolio, setSelectedPortfolio] = useState(
    initialSelectedPortfolio,
  )

  // GraphQL mutation
  const [copyInvestor] = useMutation(COPY_INVESTOR)

  // Handle form submission
  const handleSubmit = async (additionalState = {}) => {
    if (!selectedPortfolio) {
      enqueueSnackbar('Please select a portfolio', {
        variant: 'warning',
      })
      return
    }

    try {
      const result = await copyInvestor({
        awaitRefetchQueries: true,
        variables: {
          investorId: investorId,
          portfolioId: selectedPortfolio,
          investorInput: additionalState, // Pass additional fields
        },
        refetchQueries: [
          {
            query: GET_INVESTORS_BY_PORTFOLIO,
            variables: { portfolioId: selectedPortfolio },
          },
          { query: GET_PORTFOLIOS },
          { query: GET_ME },
        ],
      })

      // Success handling
      enqueueSnackbar('Investor was duplicated successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      })

      if (setSelected) setSelected([])
      if (onSuccess) onSuccess(result)
    } catch (error) {
      // Error handling
      console.error('Failed to duplicate investor:', error)
      enqueueSnackbar('Investor failed to duplicate', {
        variant: 'error',
        autoHideDuration: 3000,
      })

      if (onError) onError(error)
    }
  }

  return (
    <DuplicateEntityComponent
      {...otherProps}
      entityType="investor"
      entityId={investorId}
      entity={investor}
      portfolios={portfolios}
      selectedPortfolio={selectedPortfolio}
      onPortfolioChange={setSelectedPortfolio}
      setSelected={setSelected}
      session={session}
      config={INVESTOR_CONFIG}
      onSuccess={onSuccess}
      onError={onError}
      onSubmit={handleSubmit}
      notifications={notifications}
      rightClickMenu={rightClickMenu}
      sidebarMenu={false} // Investors don't typically use sidebar menu
      underwriting={false} // Investors don't have underwriting context
      buttonText={buttonText}
    />
  )
}

// PropTypes - including rendering props
DuplicateInvestor.propTypes = {
  investorId: PropTypes.string.isRequired,
  investor: PropTypes.object,
  portfolios: PropTypes.array.isRequired,
  selectedPortfolio: PropTypes.string,
  setSelected: PropTypes.func,
  session: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  notifications: PropTypes.bool,
  rightClickMenu: PropTypes.bool,
  buttonText: PropTypes.string,
}

export default DuplicateInvestor
